import useLoadUser from '@/hooks/useLoadUser.js';
import { activeCompanyId as activeCompanyIdSelector } from '@/redux/selectors/userSelectors.js';
import clearGravityWebsiteHandoff from '@/screens/auth/lib/gravityWebsiteHandoff/clearGravityWebsiteHandoff.js';
import consumeGravityWebsiteHandoffIdFromSearch from '@/screens/auth/lib/gravityWebsiteHandoff/consumeGravityWebsiteHandoffIdFromSearch.js';
import deliverGravityWebsiteHandoff from '@/screens/auth/lib/gravityWebsiteHandoff/deliverGravityWebsiteHandoff.js';
import { GRAVITY_WEBSITE_HANDOFF_READY_EVENT } from '@/screens/auth/lib/gravityWebsiteHandoff/gravityWebsiteHandoffConstants.js';
import isGravityWebsiteHandoffId from '@/screens/auth/lib/gravityWebsiteHandoff/isGravityWebsiteHandoffId.js';
import readGravityWebsiteHandoff from '@/screens/auth/lib/gravityWebsiteHandoff/readGravityWebsiteHandoff.js';
import readGravityWebsiteHandoffIdForSignup from '@/screens/auth/lib/gravityWebsiteHandoff/readGravityWebsiteHandoffIdForSignup.js';
import readGravityWebsiteHandoffIdFromSearch from '@/screens/auth/lib/gravityWebsiteHandoff/readGravityWebsiteHandoffIdFromSearch.js';
import shouldClearGravityWebsiteHandoff from '@/screens/auth/lib/gravityWebsiteHandoff/shouldClearGravityWebsiteHandoff.js';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const HANDOFF_RETRY_BASE_MS = 10_000;
const HANDOFF_RETRY_MAX_MS = 5 * 60 * 1000;

const GravityWebsiteSignupHandoffHandler = () => {
  const location = useLocation();
  const { user } = useUser();
  const { isLoaded, isLoggedIn } = useLoadUser();
  const activeCompanyId = useSelector(activeCompanyIdSelector);
  const [deliveryAttempt, setDeliveryAttempt] = useState(0);
  const [readyHandoffId, setReadyHandoffId] = useState('');
  const retryCountRef = useRef(0);
  const clerkHandoffId = String(user?.unsafeMetadata?.website_handoff_id || '').trim();

  useLayoutEffect(() => {
    if (location.pathname !== '/signup') {
      return;
    }

    const handoffId = readGravityWebsiteHandoffIdFromSearch(location.search);

    if (handoffId) {
      consumeGravityWebsiteHandoffIdFromSearch({
        hash: location.hash,
        pathname: location.pathname,
        search: location.search
      });
      return;
    }

    if (!readGravityWebsiteHandoffIdForSignup()) {
      clearGravityWebsiteHandoff();
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const handleReady = (event) => {
      const handoffId = String(event.detail?.handoffId || '').trim();
      if (!isGravityWebsiteHandoffId(handoffId)) {
        return;
      }

      setReadyHandoffId(handoffId);
      setDeliveryAttempt((attempt) => attempt + 1);
    };

    window.addEventListener(GRAVITY_WEBSITE_HANDOFF_READY_EVENT, handleReady);
    return () => {
      window.removeEventListener(GRAVITY_WEBSITE_HANDOFF_READY_EVENT, handleReady);
    };
  }, []);

  useEffect(() => {
    if (isLoaded !== true || isLoggedIn !== true || !activeCompanyId) {
      return undefined;
    }

    const handoff = readGravityWebsiteHandoff();
    const handoffId = readyHandoffId || (handoff?.ready ? handoff.handoffId : '');

    if (!isGravityWebsiteHandoffId(handoffId) || handoffId !== clerkHandoffId) {
      return undefined;
    }

    const savedAt = handoff?.handoffId === handoffId ? handoff.savedAt : Date.now();
    let canceled = false;
    let retryTimeout;

    deliverGravityWebsiteHandoff({ handoffId }).then((result) => {
      if (canceled) {
        return;
      }

      if (shouldClearGravityWebsiteHandoff({ result, savedAt })) {
        clearGravityWebsiteHandoff(handoffId);
        setReadyHandoffId('');
        retryCountRef.current = 0;
        return;
      }

      const retryDelay = Math.min(HANDOFF_RETRY_BASE_MS * 2 ** Math.min(retryCountRef.current, 5), HANDOFF_RETRY_MAX_MS) + Math.floor(Math.random() * 1000);
      retryCountRef.current += 1;
      retryTimeout = window.setTimeout(() => {
        setDeliveryAttempt((attempt) => attempt + 1);
      }, retryDelay);
    });

    return () => {
      canceled = true;
      window.clearTimeout(retryTimeout);
    };
  }, [activeCompanyId, clerkHandoffId, deliveryAttempt, isLoaded, isLoggedIn, readyHandoffId]);

  return null;
};

export default GravityWebsiteSignupHandoffHandler;
